import React, { useState, useMemo, useEffect } from "react";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import FullScreenPopup from "../../../layout/components/FullScreenPopup/FullScreenPopup";
import { Trans, useTranslation } from "@coworker/locales";
import styles from "./newuserpage.module.css";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import { FixaListViewItem } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListViewItem";
import { Store, excludedStoreIds } from "../../../shared/types/store";
import { SimpleHeader } from "../../../layout/components/SimpleHeader/SimpleHeader";
import { FixaText } from "../../../shared/wrappers/FixaText/FixaText";
import { useAccount, useMsal } from "@azure/msal-react";
import { AccountInfo } from "@azure/msal-browser";
import { useQueryClient } from "@tanstack/react-query";
import { useLoggedInUser } from "@coworker/app/src/core/auth/useLoggedInUser";
import useFixaNavigate from "@coworker/apprestructured/src/shared/wrappers/FixaNavigate/useFixaNavigate";
import { UserProfile } from "@coworker/types/lib/userProfileTypes";
import { useAtkomstCoreStores } from "../../hooks/useAtkomstCoreStores";
import { useCreateUser } from "@coworker/apprestructured/src/shared/hooks/users/useCreateUser";
import FixaSearch from "@coworker/apprestructured/src/shared/wrappers/FixaSearch/FixaSearch";
import FixaInlineMessage from "@coworker/apprestructured/src/shared/wrappers/FixaInlineMessage/FixaInlineMessage";
import InformationCircleIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/InformationCircleIconPath";
import useUserHasRoleOnPlatformOwner from "../../hooks/useUserHasRoleOnPlatformOwner";

export const NewUserPage: React.FC = () => {
  const { data: hasPlatformOwnerAuthorization } =
    useUserHasRoleOnPlatformOwner();
  const {
    data: storesResponse,
    isLoading,
    isError,
  } = useAtkomstCoreStores(!!hasPlatformOwnerAuthorization);
  const { accounts } = useMsal();
  const msalAccount = useAccount(accounts[0] || {}) as AccountInfo;
  const navigate = useFixaNavigate();
  const queryClient = useQueryClient();
  const { setLoggedInUser, loggedInUser } = useLoggedInUser();
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedStore, setSelectedStore] = useState<string>("");
  const {
    mutateAsync: createUserMutation,
    isPending: isCreatingUser,
    isError: isCreateUserError,
  } = useCreateUser();

  let email =
    msalAccount?.idTokenClaims?.preferred_username ??
    msalAccount?.username ??
    "";
  if (email?.split("@")[1]?.toLowerCase() === "ikea.com") {
    email = email.replace("ikea", "ingka.ikea");
  }
  const uid = msalAccount?.idTokenClaims?.oid ?? "";

  // Check if user already has a store assigned, redirect to home if they do
  useEffect(() => {
    if (loggedInUser.storeId && loggedInUser.teamId) {
      navigate("/home");
    }
  }, [loggedInUser.storeId, loggedInUser.teamId, navigate]);

  const stores = useMemo(() => {
    return (storesResponse ?? [])
      .filter((store: Store) => {
        if (
          hasPlatformOwnerAuthorization &&
          store.storeId === "012#fixa-team"
        ) {
          return true;
        }

        return !excludedStoreIds.includes(store.storeId);
      })
      .sort((a: Store, b: Store) => a.name.localeCompare(b.name));
  }, [storesResponse, hasPlatformOwnerAuthorization]);

  const filteredStores = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    if (!normalizedQuery) {
      return stores;
    }

    return stores.filter((store: Store) => {
      const name = store.name?.toLowerCase() ?? "";
      const storeId = store.storeId?.toLowerCase() ?? "";

      return (
        name.includes(normalizedQuery) || storeId.includes(normalizedQuery)
      );
    });
  }, [stores, searchQuery]);

  const handleStoreSelect = (storeId: string) => {
    setSelectedStore(storeId);
  };

  const handleContinue = async () => {
    const selectedStoreData = stores.find(
      (store: Store) => store.storeId === selectedStore
    );

    const userToCreate = {
      roleId: "COWORKER",
      emails: [email],
      teamId: "",
      storeId: selectedStoreData?.storeId ?? "",
      status: "ACTIVE",
    };

    const response = await createUserMutation(userToCreate);
    const createdUser = response?.[0];

    if (createdUser?.uid) {
      setLoggedInUser(createdUser as UserProfile);
    }

    await queryClient.invalidateQueries({ queryKey: ["user", email, uid] });

    navigate("/teamselection");
  };

  return (
    <div className={styles["new-user-page-wrapper"]}>
      <FullScreenPopup
        actionBarContent={
          !isLoading && (
            <FixaButton
              aria-label={"selectstore"}
              className={styles["action-bar-continue-button"] || ""}
              disabled={!selectedStore || isCreatingUser}
              text={<Trans>continue2String</Trans>}
              type="primary"
              onClick={handleContinue}
            />
          )
        }
        noPadding
        noAppBar
      >
        <SimpleHeader displayAllScreens />
        <div className={styles["content-wrapper"]}>
          <div className={styles["header-section"]}>
            <FixaText tagName="h1" textStyle="Heading.L">
              {t("selectStoreString")}
            </FixaText>
            <FixaText tagName="p" textStyle="Body.M">
              {t("selectStoreDescriptionString")}
            </FixaText>
            <FixaInlineMessage
              className={styles["store-disclaimer"]}
              variant="informative"
              ssrIcon={InformationCircleIconPath}
              body={
                <>
                  {t("atkomstStoresDisclaimerString")}
                  <a
                    href="https://myidentity.apps.ikea.com/IdentityManager"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    MyIdentity
                  </a>
                  .
                </>
              }
            />
          </div>

          {isLoading ? (
            <div className={styles["loading-container"]}>
              <FixaText tagName="p" textStyle="Body.M">
                {t("loadingString")}...
              </FixaText>
            </div>
          ) : isError ? (
            <div className={styles["error-container"]}>
              <FixaText
                tagName="p"
                textStyle="Body.M"
                className={styles["error-message"]}
              >
                {t("errorLoadingStoresString")}
              </FixaText>
            </div>
          ) : isCreateUserError ? (
            <div className={styles["error-container"]}>
              <FixaText
                tagName="p"
                textStyle="Body.M"
                className={styles["error-message"]}
              >
                {t("isCreateUserError")}
              </FixaText>
            </div>
          ) : stores.length === 0 ? (
            <div className={styles["loading-container"]}>
              <FixaText tagName="p" textStyle="Body.M">
                {t("noPermittedStores")}
              </FixaText>
            </div>
          ) : (
            <>
              <div className={styles["search-wrapper"]}>
                <FixaSearch
                  id="new-user-store-search"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  onClear={() => setSearchQuery("")}
                  placeholder={t("searchBasicString")}
                />
              </div>

              {filteredStores.length === 0 ? (
                <div className={styles["loading-container"]}>
                  <FixaText tagName="p" textStyle="Body.M">
                    {t("noMatchingStores")}
                  </FixaText>
                </div>
              ) : (
                <FixaListView id="storeSelector">
                  {filteredStores.map((store: Store) => (
                    <FixaListViewItem
                      id={`store-${store.storeId}`}
                      inset
                      key={store.storeId}
                      title={`${store.name} (${store.storeId})`}
                      control="radiobutton"
                      controlProps={{
                        checked: selectedStore === store.storeId,
                        value: store.storeId,
                      }}
                      onChange={() => handleStoreSelect(store.storeId)}
                    />
                  ))}
                </FixaListView>
              )}
            </>
          )}
        </div>
      </FullScreenPopup>
    </div>
  );
};
